import * as socketIoClient from "socket.io-client";
import {
  AckCallback,
  bufferToUint8Array,
  HandshakeQuery,
  UpdateRequest,
  UpdateResponse,
} from "./lib/communication";
import {
  CursorsMessage,
  ERROR_RESPONSE_SCHEMA,
  ErrorResponse,
  MoveCursor,
} from "./protocol";
import { Frontiers, UndoManager, VersionVector } from "loro-crdt";
import * as fp from "lodash/fp.js";
import { GetPreviewsResponseData } from "./lib/coverage";
import { MpDoc } from "./doc";
import {
  DUPLICATE_TO_DRAFT,
  GET_FRONTIERS_PACKET,
  parsePacket,
  REFRESH_PACKET,
  RefreshPacket,
  sendPacket,
  SYNC_PACKET,
  SyncPacket,
  UPDATE_HISTORY,
  UpdateHistoryPacket,
} from "./lib/packet";

export * from "loro-crdt";

type ClientEventListeners = {
  connect?: () => void;
  connectError?: (err: Error) => void;
  disconnect?: () => void;
  error?: (err: ErrorResponse) => void;
  sync?: ({ doc, map2d }: { doc: MpDoc; map2d: SyncPacket["map2d"] }) => void;
  update?: (
    bytes: { main?: Uint8Array; temp?: Uint8Array },
    senderId?: string,
    cursor?: string,
  ) => void;
  /**
   * `cursors` is for other users only
   */
  cursors?: (cursors: Map<string, string>) => void;
  deleteCursor?: (clientId: string) => void;
  previews?: (data: { previews: GetPreviewsResponseData["previews"] }) => void;
  updateHistory?: (history: UpdateHistoryPacket["history"]) => void;
  refresh?: (packet: RefreshPacket) => void;
  priceOverrideBroadcast?: (payload: { newVersionId: number }) => void;
  pricingDefaultsBroadcast?: () => void;
  versionCreatedBroadcast?: (payload: {
    actorName: string;
    blank?: boolean;
  }) => void;
  versionRevertBroadcast?: (payload: {
    actorName: string;
    targetVersionId: number;
    targetTimestamp: string;
  }) => void;
};

type ClientOpts = {
  query: HandshakeQuery;
  url: string;
  autoOn: boolean;
  eventListeners?: ClientEventListeners;
};

export class Client {
  id: string;
  io: socketIoClient.Socket;
  lastMainVersion?: VersionVector;
  lastTempVersion?: VersionVector;
  doc: MpDoc | null = null;
  undo?: UndoManager;
  eventListeners: ClientEventListeners;

  constructor(opts: ClientOpts) {
    this.id = opts.query.clientId;
    this.io = socketIoClient.io(opts.url, {
      transports: ["websocket"],
      path: "/ws",
      upgrade: false,
      secure: true,
      // note that null/undefined values get converted to strings 'null' or 'undefined' by socket.io
      //  we do not want this, so omit these values
      query: fp.omitBy(fp.isNil, opts.query),
    });
    if (opts.autoOn) {
      this.on();
    }
    this.eventListeners = opts.eventListeners ?? {};
  }

  on() {
    this.io.on("connect", () => this.eventListeners?.connect?.());
    this.io.on("connect_error", (err) =>
      this.eventListeners?.connectError?.(err),
    );
    this.io.on("disconnect", () => this.eventListeners?.disconnect?.());
    this.io.on("error", (err_) => {
      const err = ERROR_RESPONSE_SCHEMA.parse(err_);
      console.error("from server:", err.message);
      this.eventListeners?.error?.(err);
    });
    this.io.on("sync", (packetRaw, cb: AckCallback) => {
      const packet = parsePacket(SYNC_PACKET, packetRaw);
      const mainState = bufferToUint8Array(packet.mainState);
      const tempState = bufferToUint8Array(packet.tempState);
      this.doc = MpDoc.fromSnapshot({ state: mainState, tmpState: tempState });
      this.undo = this.doc.createUndoManager();
      cb({ status: "success" });
      this.eventListeners?.sync?.({ doc: this.doc, map2d: packet.map2d });
    });
    this.io.on("update", ({ bytes, senderId, cursor }: UpdateResponse) => {
      if (!this.doc) {
        console.warn(`[update] tried to handle update before sync`);
        return;
      }
      const main = bytes.main ? bufferToUint8Array(bytes.main) : undefined;
      const temp = bytes.temp ? bufferToUint8Array(bytes.temp) : undefined;
      this.eventListeners?.update?.({ main, temp }, senderId, cursor);
    });
    this.io.on("cursors", (message: CursorsMessage) => {
      const cursors = new Map(Object.entries(message.cursors));
      cursors.delete(this.id);
      if (cursors.size > 0) this.eventListeners?.cursors?.(cursors);
    });
    this.io.on("delete_cursor", (clientId: string) => {
      this.eventListeners?.deleteCursor?.(clientId);
    });
    this.io.on("previews", (data) => {
      this.eventListeners?.previews?.(data);
    });
    this.io.on("update_history", (data: unknown) => {
      const { history } = parsePacket(UPDATE_HISTORY, data);
      this.eventListeners?.updateHistory?.(history);
    });
    this.io.on("refresh", (data: unknown) => {
      this.eventListeners?.refresh?.(parsePacket(REFRESH_PACKET, data));
    });
    this.io.on(
      "price_override_broadcast",
      (payload: { newVersionId: number }) => {
        this.eventListeners?.priceOverrideBroadcast?.(payload);
      },
    );
    this.io.on("pricing_defaults_broadcast", () => {
      this.eventListeners?.pricingDefaultsBroadcast?.();
    });
    this.io.on(
      "version_created_broadcast",
      (payload: { actorName: string; blank?: boolean }) => {
        this.eventListeners?.versionCreatedBroadcast?.(payload);
      },
    );
    this.io.on(
      "version_revert_broadcast",
      (payload: {
        actorName: string;
        targetVersionId: number;
        targetTimestamp: string;
      }) => {
        this.eventListeners?.versionRevertBroadcast?.(payload);
      },
    );
  }

  off() {
    this.io.off("connect");
    this.io.off("connect_error");
    this.io.off("disconnect");
    this.io.off("error");
    this.io.off("sync");
    this.io.off("update");
  }

  signalUpdate(opts: { instant?: boolean; contributorId?: number } = {}) {
    const instant = opts.instant ?? false;
    const contributorId = opts.contributorId;

    if (!this.doc) {
      // we haven't synced yet
      console.warn("[updateState] tried to update state before sync");
      return;
    }
    const { state, tmpState, stateVersion, tmpStateVersion } = this.doc.export({
      stateFromVersion: this.lastMainVersion,
      tmpStateFromVersion: this.lastTempVersion,
    });
    this.lastMainVersion = stateVersion;
    this.lastTempVersion = tmpStateVersion;

    this.io.emit("update", {
      main: state,
      temp: tmpState,
      instant,
      contributorId,
    } satisfies UpdateRequest);
  }

  restoreVersion(frontiers: Frontiers) {
    if (!this.doc) return;
    this.doc.restore(frontiers);
    // this.io.emit("restore", sendPacket(RESTORE_VERSION_REQUEST_PACKET, {
    //   frontiers,
    // }));
  }

  logRevert(targetVersionId: number, targetTimestamp: string) {
    this.io.emit("log_revert", { targetVersionId, targetTimestamp });
  }

  duplicateToDraft({
    versionId,
    blank,
  }: {
    versionId: number;
    blank?: boolean;
  }) {
    if (!this.doc) return;
    this.io.emit(
      "duplicate_to_draft",
      sendPacket(DUPLICATE_TO_DRAFT, {
        versionId,
        previousFrontiers: this.doc.frontiers(),
        blank,
      }),
    );
  }

  signalCursorMove(position: string) {
    this.io.emit("move_cursor", { position } satisfies MoveCursor);
  }

  async getServerFrontiers() {
    const response = parsePacket(
      GET_FRONTIERS_PACKET,
      await this.io.emitWithAck("get_frontiers"),
    );
    return response.frontiers;
  }

  setEventListener<K extends keyof ClientEventListeners>(
    event: K,
    listener: ClientEventListeners[K],
  ) {
    this.eventListeners[event] = listener;
  }
}

export type VersionHistory = SyncPacket["map2d"]["history"];
